import { SEOHead } from '@/components/seo-head'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Search,
  Shield,
  Star,
  ArrowRight,
  Globe,
  MessageSquare,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  DollarSign,
  Target,
  BarChart3,
  MapPin,
  Languages,
  Smartphone,
  Database,
  Eye,
  Lock,
  RefreshCw,
  Share2
} from 'lucide-react'

export default function EuropagesAlternative() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "SourcedIn - Europages Alternative",
    "description": "Advanced B2B directory and marketplace alternative to Europages. AI-powered supplier discovery, verified companies, and comprehensive business solutions for European and global markets.",
    "url": "https://sourcedin.ai/europages-alternative",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "ratingCount": "2100",
      "bestRating": "5"
    },
    "author": {
      "@type": "Organization",
      "name": "SourcedIn",
      "url": "https://sourcedin.ai"
    },
    "browserRequirements": "Requires JavaScript. Requires HTML5.",
    "softwareVersion": "2.0",
    "datePublished": "2024-01-15",
    "dateModified": "2024-01-15"
  }

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What makes SourcedIn better than Europages?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "SourcedIn offers AI-powered search, real-time company verification, advanced filtering, modern user interface, and comprehensive business tools compared to Europages' outdated platform and limited functionality."
        }
      },
      {
        "@type": "Question",
        "name": "Is SourcedIn free to use?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, SourcedIn offers a generous free tier with basic search and company profiles. Premium plans start at $99/month for advanced features, unlimited searches, and priority support."
        }
      },
      {
        "@type": "Question",
        "name": "Does SourcedIn cover European markets like Europages?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, SourcedIn provides comprehensive coverage of European markets and beyond. Our platform includes verified companies from all EU countries, UK, and global markets with advanced localization features."
        }
      },
      {
        "@type": "Question",
        "name": "How does SourcedIn verify companies?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "SourcedIn uses automated verification, third-party data sources, compliance monitoring, customer reviews, and direct verification processes to ensure all listed companies are legitimate and up-to-date."
        }
      }
    ]
  }

  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://sourcedin.ai"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "B2B Directory",
        "item": "https://sourcedin.ai/b2b-marketplace"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Europages Alternative",
        "item": "https://sourcedin.ai/europages-alternative"
      }
    ]
  }

  return (
    <>
      <SEOHead
        title="Best Europages Alternative 2024 | SourcedIn - AI-Powered B2B Directory & Marketplace"
        description="Discover the best Europages alternative with SourcedIn. AI-powered company search, verified suppliers, advanced filtering, and comprehensive B2B directory for European and global markets. Free trial available."
        keywords="europages alternative, europages replacement, b2b directory europe, company directory, supplier directory europe, business directory, verified companies, ai powered search, european suppliers, global marketplace"
        canonical="https://sourcedin.ai/europages-alternative"
        structuredData={[structuredData, faqStructuredData, breadcrumbStructuredData]}
      />

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
        {/* Breadcrumb */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <nav className="flex items-center space-x-2 text-sm text-gray-600">
              <a href="/" className="hover:text-primary">Home</a>
              <span>/</span>
              <a href="/b2b-marketplace" className="hover:text-primary">B2B Directory</a>
              <span>/</span>
              <span className="text-gray-900 font-medium">Europages Alternative</span>
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <section className="relative py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center px-4 py-2 bg-primary/10 text-primary border border-primary/20 rounded-full text-sm font-medium mb-6">
                <Globe className="w-4 h-4 mr-2" />
                #1 Europages Alternative 2024
              </div>
              
              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                The Ultimate <span className="text-primary">Europages Alternative</span> for Modern B2B Discovery
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
                Stop struggling with Europages' outdated interface and limited features. SourcedIn delivers 
                AI-powered company search, real-time verification, and modern tools that actually work for 
                today's European and global business professionals.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-white px-8 py-4 text-lg">
                  Try SourcedIn Free
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button size="lg" variant="outline" className="px-8 py-4 text-lg">
                  Watch Demo
                </Button>
              </div>

              <div className="flex items-center justify-center space-x-8 text-sm text-gray-500">
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                  Free 14-day trial
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                  No credit card required
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                  Setup in 5 minutes
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Comparison Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Europages vs SourcedIn: Why Make the Switch?
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                See why thousands of European businesses are switching from Europages to SourcedIn
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-16">
              <Card className="border-red-200 bg-red-50">
                <CardHeader>
                  <CardTitle className="flex items-center text-red-700">
                    <XCircle className="w-5 h-5 mr-2" />
                    Europages Limitations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <XCircle className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Outdated design and poor user experience from the early 2000s</span>
                    </li>
                    <li className="flex items-start">
                      <XCircle className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Limited search functionality with poor filtering options</span>
                    </li>
                    <li className="flex items-start">
                      <XCircle className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>No real-time company verification or status updates</span>
                    </li>
                    <li className="flex items-start">
                      <XCircle className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Expensive premium features with limited value</span>
                    </li>
                    <li className="flex items-start">
                      <XCircle className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Poor mobile experience and outdated technology</span>
                    </li>
                    <li className="flex items-start">
                      <XCircle className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Limited integration capabilities with modern business tools</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-green-200 bg-green-50">
                <CardHeader>
                  <CardTitle className="flex items-center text-green-700">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    SourcedIn Advantages
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Modern, intuitive interface designed for 2024</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>AI-powered search with instant, accurate results</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Real-time company verification and compliance monitoring</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Transparent pricing with powerful free tier</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Mobile-first design with cutting-edge technology</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Seamless integration with CRM, ERP, and business tools</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Advanced Features That Europages Can't Match
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Built for modern European businesses who need results, not frustration
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <Search className="w-6 h-6 text-blue-600" />
                  </div>
                  <CardTitle>AI-Powered Company Search</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Find companies 15x faster with intelligent search that understands your requirements 
                    and suggests the best matches across European and global markets.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                    <Shield className="w-6 h-6 text-green-600" />
                  </div>
                  <CardTitle>Real-Time Company Verification</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Every company is verified in real-time with business registrations, compliance status, 
                    financial health indicators, and up-to-date contact information.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                    <Globe className="w-6 h-6 text-purple-600" />
                  </div>
                  <CardTitle>Multi-Language Support</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Full support for 25+ European languages with automatic translation, 
                    localized search results, and region-specific business insights.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                    <BarChart3 className="w-6 h-6 text-orange-600" />
                  </div>
                  <CardTitle>Advanced Analytics & Insights</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Track your business development performance with detailed analytics, 
                    market intelligence reports, and competitor analysis tools.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                    <MessageSquare className="w-6 h-6 text-red-600" />
                  </div>
                  <CardTitle>Direct Business Communication</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Connect directly with companies through our built-in messaging system. 
                    No more playing phone tag or waiting for email responses across time zones.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                    <Database className="w-6 h-6 text-indigo-600" />
                  </div>
                  <CardTitle>Comprehensive Company Database</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Access millions of verified companies across Europe and globally with 
                    detailed profiles, certifications, and business intelligence.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <MapPin className="w-6 h-6 text-teal-600" />
                  </div>
                  <CardTitle>Geographic & Industry Filtering</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Advanced filtering by location, industry, company size, certifications, 
                    and more to find exactly what you're looking for.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center mb-4">
                    <Smartphone className="w-6 h-6 text-pink-600" />
                  </div>
                  <CardTitle>Mobile-First Experience</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Fully responsive design optimized for mobile devices with offline capabilities 
                    and push notifications for important updates.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4">
                    <RefreshCw className="w-6 h-6 text-yellow-600" />
                  </div>
                  <CardTitle>Real-Time Data Updates</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Company information is updated in real-time from multiple sources including 
                    business registries, news feeds, and direct company updates.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* European Coverage Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Comprehensive European & Global Coverage
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Access verified companies from all European countries and beyond
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">All EU Countries</h3>
                <p className="text-gray-600 text-sm">
                  Complete coverage of all 27 EU member states with local business registries and compliance data.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">UK & EEA</h3>
                <p className="text-gray-600 text-sm">
                  Comprehensive data from UK, Norway, Switzerland, and other European Economic Area countries.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Languages className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">25+ Languages</h3>
                <p className="text-gray-600 text-sm">
                  Native language support for all major European languages with automatic translation.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-8 h-8 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Global Expansion</h3>
                <p className="text-gray-600 text-sm">
                  Growing coverage of North America, Asia-Pacific, and emerging markets worldwide.
                </p>
              </div>
            </div>

            <div className="mt-16 bg-gradient-to-r from-blue-600 to-green-600 rounded-2xl p-8 text-white">
              <div className="text-center">
                <h3 className="text-2xl font-bold mb-4">Verified Companies Across Europe</h3>
                <div className="grid md:grid-cols-3 gap-8">
                  <div className="text-center">
                    <div className="text-3xl font-bold mb-2">2.5M+</div>
                    <div className="text-blue-100">Verified European Companies</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold mb-2">150+</div>
                    <div className="text-blue-100">Industries Covered</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold mb-2">99.2%</div>
                    <div className="text-blue-100">Data Accuracy Rate</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gradient-to-r from-blue-600 to-green-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Why Choose SourcedIn Over Europages?
              </h2>
              <p className="text-xl opacity-90 max-w-2xl mx-auto">
                Join thousands of European businesses who've made the switch
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-semibold mb-2">15x Faster Search</h3>
                <p className="opacity-90">
                  Find companies in seconds, not minutes. Our AI-powered search delivers 
                  relevant results instantly across all European markets.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <DollarSign className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Save 40% on Costs</h3>
                <p className="opacity-90">
                  Better company selection and negotiation tools help you find 
                  better partners and reduce business development costs.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Higher Quality Partners</h3>
                <p className="opacity-90">
                  Real-time verification ensures you only work with legitimate, 
                  compliant companies who deliver on their promises.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Eye className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Better Visibility</h3>
                <p className="opacity-90">
                  Advanced SEO optimization and modern platform features help 
                  your company get discovered by the right partners.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Enhanced Security</h3>
                <p className="opacity-90">
                  GDPR compliant with enterprise-grade security, data encryption, 
                  and privacy protection for all European users.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Share2 className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Easy Integration</h3>
                <p className="opacity-90">
                  Seamless integration with your existing CRM, ERP, and business 
                  tools through our comprehensive API and connectors.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                What Our European Customers Say About Switching from Europages
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Real feedback from business professionals who made the switch
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <blockquote className="text-gray-600 mb-4">
                    "Switching from Europages to SourcedIn was the best decision for our German manufacturing company. 
                    The search is incredibly fast and the interface actually makes sense. We've found 3x more 
                    qualified suppliers in half the time."
                  </blockquote>
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-blue-600 font-semibold">HM</span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Hans Mueller</p>
                      <p className="text-sm text-gray-500">Procurement Director, Munich Manufacturing</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <blockquote className="text-gray-600 mb-4">
                    "Europages felt like it was stuck in 2005. SourcedIn has everything we need for our 
                    French distribution business. The company verification feature alone has saved us 
                    countless hours of due diligence."
                  </blockquote>
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-green-600 font-semibold">MR</span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Marie Rousseau</p>
                      <p className="text-sm text-gray-500">Business Development Manager, Paris Distribution</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <blockquote className="text-gray-600 mb-4">
                    "The AI-powered search is incredible for our UK technology company. It actually understands 
                    what we're looking for and suggests partners we never would have found on Europages. 
                    Revolutionary technology for European B2B."
                  </blockquote>
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-purple-600 font-semibold">JW</span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">James Wilson</p>
                      <p className="text-sm text-gray-500">Partnership Manager, London Tech Solutions</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-gray-600">
                Everything you need to know about switching from Europages to SourcedIn
              </p>
            </div>

            <div className="space-y-8">
              <div className="border-b border-gray-200 pb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  What makes SourcedIn better than Europages?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  SourcedIn offers a modern, intuitive interface with AI-powered search capabilities, 
                  real-time company verification, advanced filtering options, comprehensive analytics, 
                  and seamless integration with modern business tools. Unlike Europages' outdated platform, 
                  SourcedIn is built for today's European business professionals who need speed, accuracy, and results.
                </p>
              </div>

              <div className="border-b border-gray-200 pb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Is SourcedIn free to use?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Yes! SourcedIn offers a generous free tier that includes basic search functionality, 
                  company profiles, and essential features. Premium plans start at $99/month and 
                  include advanced analytics, unlimited searches, priority support, and team collaboration tools.
                </p>
              </div>

              <div className="border-b border-gray-200 pb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Does SourcedIn cover European markets like Europages?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Yes, SourcedIn provides comprehensive coverage of European markets and beyond. Our platform 
                  includes verified companies from all EU countries, UK, EEA nations, and global markets with 
                  advanced localization features, multi-language support, and region-specific business insights.
                </p>
              </div>

              <div className="border-b border-gray-200 pb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  How does SourcedIn verify companies?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  SourcedIn uses a comprehensive verification process including automated business registry 
                  checks, third-party data sources, compliance monitoring, customer reviews, financial health 
                  indicators, and direct verification with companies. All verification data is updated in 
                  real-time to ensure accuracy across all European markets.
                </p>
              </div>

              <div className="border-b border-gray-200 pb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Can I migrate my data from Europages?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  While we can't directly import data from Europages due to their platform limitations, 
                  our team can help you set up your company database and import your existing business 
                  contacts and supplier information. We offer free migration assistance for new customers.
                </p>
              </div>

              <div className="border-b border-gray-200 pb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Is SourcedIn GDPR compliant for European users?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Absolutely! SourcedIn is fully GDPR compliant with enterprise-grade security, data encryption, 
                  privacy protection, and data portability features. We're based in the EU and follow all 
                  European data protection regulations.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  How quickly can I get started?
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  You can start using SourcedIn immediately after signing up. Our intuitive interface 
                  means you'll be finding companies within minutes, not hours. Most European users report 
                  being fully productive within their first session.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-primary to-green-600 text-white">
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Leave Europages Behind?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Join thousands of European businesses who've already made the switch. 
              Start your free trial today and experience the difference.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button size="lg" className="bg-white text-primary hover:bg-gray-100 px-8 py-4 text-lg">
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary px-8 py-4 text-lg">
                Schedule Demo
              </Button>
            </div>

            <div className="flex items-center justify-center space-x-8 text-sm opacity-80">
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 mr-2" />
                Free 14-day trial
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 mr-2" />
                GDPR compliant
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 mr-2" />
                Cancel anytime
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}
